import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from "@firebase/firestore";
import { dismissLoadingToast, showErrorToast, showLoadingToast, showSuccessToast, updateLoadingToast } from "app/components/ShowToast";
import axios from "axios";
import db from "services/firebase";

interface CloudflareUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  status: string;
  two_factor_authentication_enabled?: boolean;
  roles: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

interface CloudflareDomain {
  id: string;
  name: string;
  status: string;
  type: string;
  account?: {
    id: string;
    name: string;
  };
  created_on: string;
  modified_on: string;
  activated_on?: string;
}

interface CloudflareDataResponse {
  success: boolean;
  users?: CloudflareUser[];
  domains?: CloudflareDomain[];
  error?: string;
}

interface CloudflareError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      error?: string;
      errors?: Array<{ message?: string; code?: number }>;
    };
  };
  message?: string;
}

export const createCloudflareIntegration = (apiToken: string) => {
  if (!apiToken || typeof apiToken !== "string" || apiToken.trim() === "") {
    throw new Error("A valid API token is required for Cloudflare integration");
  }

  const getUserFriendlyErrorMessage = (error: unknown): string => {
    const cloudflareError = error as CloudflareError;
    if (cloudflareError.response?.status === 403) {
      return "Access forbidden. Please check your API token permissions.";
    } else if (cloudflareError.response?.status === 401) {
      return "Invalid API token. Please check your token and try again.";
    } else if (cloudflareError.response?.status === 404) {
      return "Resource not found. Please check the account ID or resource.";
    } else if (cloudflareError.response?.status === 429) {
      return "Cloudflare API rate limit exceeded. Please try again later.";
    } else if (cloudflareError.message?.includes("ENOTFOUND") || cloudflareError.message?.includes("Network Error")) {
      return "Unable to reach Cloudflare API. Please check your network connection and try again.";
    }
    
    const errorMessages = cloudflareError.response?.data?.errors?.map(e => e.message).filter(Boolean);
    if (errorMessages && errorMessages.length > 0) {
      return errorMessages.join(", ");
    }
    
    return (
      cloudflareError.response?.data?.message ||
      cloudflareError.response?.data?.error ||
      cloudflareError.message ||
      "An unexpected error occurred."
    );
  };

  const fetchCloudflareData = async (): Promise<CloudflareDataResponse> => {
    try {
      const response = await axios.post(
        `https://us-central1-slate-134a0.cloudfunctions.net/fetchCloudflareData`,
        { apiToken },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      return response.data as CloudflareDataResponse;
    } catch (error: any) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  return {
    fetchCloudflareData,
  };
};

export const fetchAndSaveCloudflareData = async (
  apiTokenName: string,
  apiToken: string,
  users: CloudflareUser[],
  domains: CloudflareDomain[],
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
): Promise<string | undefined> => {
  try {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    let integrationId: string | undefined;

    const tenantRef = doc(db, "tenants", currentUser?.tenantId);
    const integrationsRef = collection(tenantRef, "integrations");
    const vendorsRef = collection(tenantRef, "vendors");
    const accessReviewRef = doc(tenantRef, "access_review", "cloudflare");
    const integrationSnapshot = await getDocs(
      query(integrationsRef, where("name", "==", "Cloudflare"))
    );

    if (integrationSnapshot.empty) {
      const newData = {
        apiTokenName: apiTokenName,
        apiToken: apiToken,
        name: "Cloudflare",
        group: "Cloud Providers",
        img_path: "/integrations/cloudflare_Logo.avif",
        status: "Connected",
        connectedAt: formattedDate,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
        userCount: users.length,
        domainCount: domains.length,
      };

      const docRef = await addDoc(integrationsRef, newData);
      integrationId = docRef.id;

      const vendorData = {
        authentication_type: "API Token",
        category: "Cloud Providers",
        created_at: serverTimestamp(),
        due_diligence_status: "Incomplete",
        impact_assessment: [],
        logo: [
          {
            name: "cloudflare_Logo.avif",
            size: 5000,
            url: "/integrations/cloudflare_Logo.avif",
          },
        ],
        owner: currentUser?.userName || "",
        policy_url: "https://www.cloudflare.com/terms/",
        reliance: "",
        risk_level: "",
        tenantId: currentUser?.tenantId,
        terms_url: "https://www.cloudflare.com/terms/",
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: "Cloudflare",
        website_url: "https://www.cloudflare.com",
      };

      await addDoc(vendorsRef, vendorData);
    } else {
      integrationId = integrationSnapshot.docs[0].id;
      // Update existing integration
      await updateDoc(doc(integrationsRef, integrationId), {
        apiTokenName: apiTokenName,
        apiToken: apiToken,
        userCount: users.length,
        domainCount: domains.length,
        updatedAt: serverTimestamp(),
      });
    }

    // Save users to access_review collection
    const membersMap: { [key: string]: any } = {};
    for (const user of users || []) {
      if (user.id && user.email) {
        const roleNames = user.roles?.map(role => role.name).join(", ") || "Member";
        
        membersMap[user.id] = {
          id: user.id,
          email: user.email,
          name: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          accountRole: roleNames,
          roles: user.roles || [],
          status: user.status || 'active',
          two_factor_authentication_enabled: user.two_factor_authentication_enabled || false,
          isEnrolledIn2FA: user.two_factor_authentication_enabled || false,
          platform: "Cloudflare",
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
      }
    }

    // Save or update access review document
    const cloudflareDoc = await getDoc(accessReviewRef);
    if (cloudflareDoc.exists()) {
      await updateDoc(accessReviewRef, {
        members: {
          ...cloudflareDoc.data().members,
          ...membersMap,
        },
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(accessReviewRef, {
        platform: "Cloudflare",
        members: membersMap,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    // Save domains to domains collection and assets collection
    if (domains && domains.length > 0 && integrationId) {
      const domainsRef = collection(tenantRef, "domain");
      const assetsRef = collection(tenantRef, "assets");
      const currentDate = new Date();
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`;
      
      for (const domain of domains) {
        if (domain.name) {
          // Check if domain already exists in domains collection
          const existingDomainQuery = query(
            domainsRef,
            where("domainName", "==", domain.name),
            where("tenantId", "==", currentUser?.tenantId)
          );
          const existingDomainSnapshot = await getDocs(existingDomainQuery);

          const domainData = {
            name: domain.name,
            platform: "Cloudflare",
            type: "domain",
            domainName: domain.name,
            zoneId: domain.id,
            status: domain.status || 'unknown',
            zoneType: domain.type || 'full',
            accountId: domain.account?.id || '',
            accountName: domain.account?.name || '',
            createdOn: domain.created_on || '',
            modifiedOn: domain.modified_on || '',
            activatedOn: domain.activated_on || '',
            integration_id: integrationId,
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          if (existingDomainSnapshot.empty) {
            // Create new domain document
            await addDoc(domainsRef, domainData);
          } else {
            // Update existing domain document
            const existingDoc = existingDomainSnapshot.docs[0];
            await updateDoc(doc(domainsRef, existingDoc.id), {
              ...domainData,
              createdAt: existingDoc.data().createdAt, // Preserve original creation time
            });
          }

          // Save domain as asset
          const assetData = {
            name: domain.name,
            platform: "Cloudflare",
            type: "domain",
            domainName: domain.name,
            zoneId: domain.id,
            status: domain.status || 'unknown',
            zoneType: domain.type || 'full',
            accountId: domain.account?.id || '',
            accountName: domain.account?.name || '',
            createdOn: domain.created_on || '',
            modifiedOn: domain.modified_on || '',
            activatedOn: domain.activated_on || '',
            integration_id: integrationId,
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            audit_scope: true,
          };

          // Check if asset already exists
          const existingAssetQuery = query(
            assetsRef,
            where("domainName", "==", domain.name),
            where("platform", "==", "Cloudflare"),
            where("tenantId", "==", currentUser?.tenantId)
          );
          const existingAssetSnapshot = await getDocs(existingAssetQuery);

          if (existingAssetSnapshot.empty) {
            // Create new asset document
            await addDoc(assetsRef, assetData);
            
            // Log activity
            const activitiesRef = collection(tenantRef, "activities");
            await addDoc(activitiesRef, {
              message: `${currentUser?.userName || 'Unknown User'} added a new asset '${domain.name}'`,
              timestamp: serverTimestamp(),
              tenantId: currentUser?.tenantId,
              uid: currentUser?.uid,
              action: "Asset added",
              target: "Asset Management",
              email: currentUser?.email || '',
              username: currentUser?.userName || 'Unknown User',
              date: todaysDate,
            });
          } else {
            // Update existing asset document
            const existingAssetDoc = existingAssetSnapshot.docs[0];
            await updateDoc(doc(assetsRef, existingAssetDoc.id), {
              ...assetData,
              createdAt: existingAssetDoc.data().createdAt, // Preserve original creation time
            });
          }
        }
      }
    }

    return integrationId;
  } catch (error) {
    showErrorToast({ message: "Error setting up Cloudflare integration" });
    navigate("/integrations", { replace: true });
    throw error;
  }
};
